/**
 * Chambers Intelligence — Reports
 * Premium legal operations intelligence. Real data, proper hierarchy, tasteful visuals.
 */

import { useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/store';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  AreaChart, Area, CartesianGrid,
} from 'recharts';
import {
  fetchHearingReadiness,
  fetchBarristerWorkload,
  fetchMatterHealth,
  fetchClerkOperations,
  fetchFinancialPerformance,
  fetchOutstandingFees,
  fetchOperationalAlerts,
  downloadCSV,
} from '@/lib/reportsApi';
import { fetchFeeNotes, STATUS_LABELS as FEE_STATUS_LABELS, type FeeNoteListRow } from '@/lib/feeNotesApi';
import {
  Warning, XCircle, DownloadSimple, ArrowRight,
  TrendUp, TrendDown, CircleNotch, Gavel,
  CurrencyGbp, Users, Briefcase, ClipboardText,
  EnvelopeSimple,
} from '@phosphor-icons/react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Default to last 12 months so financial data always shows
const twelveMonthsAgo = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
};
const today = () => new Date().toISOString().slice(0, 10);

const gbp = (n: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);
const gbpK = (n: number) => n >= 1000 ? `£${(n / 1000).toFixed(0)}k` : gbp(n);

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};

const HEARING_TYPE: Record<string, string> = {
  trial: 'Trial', case_management: 'CMC', pre_trial_review: 'PTR', ptph: 'PTPH',
  interlocutory: 'Application', sentencing: 'Sentencing', appeal: 'Appeal',
  mention: 'Mention', bail: 'Bail', costs: 'Costs',
  first_listing: 'First Listing', mediation: 'Mediation', other: 'Hearing',
};
const htLabel = (t: string) => HEARING_TYPE[t] ?? t.replace(/_/g, ' ');
const barName = (b: { firstName: string; lastName: string; memberType: string } | null) =>
  b ? `${b.firstName} ${b.lastName}${b.memberType === 'silk' ? ' KC' : ''}` : '—';

// ─── Primitive components ──────────────────────────────────────────────────────

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-[0.09em] text-muted-foreground';

function Loading() {
  return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <CircleNotch className="h-6 w-6 animate-spin" />
    </div>
  );
}

function ExportBtn({ onClick }: { onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground">
      <DownloadSimple className="h-3.5 w-3.5" /> Export CSV
    </button>
  );
}

// ─── KPI Card — the core intelligence unit ────────────────────────────────────

function KpiCard({
  icon, label, value, sub, trend, accent,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
  trend?: 'up' | 'down';
  accent?: 'red' | 'amber' | 'green' | 'blue' | 'default';
}) {
  const accentMap = {
    red:     { border: 'border-l-4 border-l-red-500',    num: 'text-red-700 dark:text-red-300',    bg: 'bg-red-50/40 dark:bg-red-500/10' },
    amber:   { border: 'border-l-4 border-l-amber-500',  num: 'text-amber-800 dark:text-amber-300',  bg: 'bg-amber-50/40 dark:bg-amber-500/10' },
    green:   { border: 'border-l-4 border-l-emerald-500',num: 'text-emerald-800 dark:text-emerald-300',bg: 'bg-emerald-50/30 dark:bg-emerald-500/10' },
    blue:    { border: 'border-l-4 border-l-brand-500',  num: 'text-brand-800 dark:text-brand-200',  bg: 'bg-brand-50/30' },
    default: { border: 'border-l-4 border-l-transparent',num: 'text-foreground', bg: 'bg-card' },
  };
  const a = accentMap[accent ?? 'default'];
  return (
    <div className={`rounded-xl border ${a.border} ${a.bg} px-5 py-4 shadow-sm`}>
      <div className="mb-3 flex items-center justify-between">
        <span className={LABEL_CLS}>{label}</span>
        <span className="text-muted-foreground/60">{icon}</span>
      </div>
      <div className={`text-3xl font-bold leading-none ${a.num}`}>
        {value}
      </div>
      {sub && (
        <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
          {trend === 'up'   && <TrendUp  className="h-3 w-3 text-emerald-500" />}
          {trend === 'down' && <TrendDown className="h-3 w-3 text-red-500" />}
          {sub}
        </div>
      )}
    </div>
  );
}

// ─── Status chips ─────────────────────────────────────────────────────────────

function Chip({ level }: { level: string }) {
  const map: Record<string, string> = {
    overloaded: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
    stalled:    'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
    critical:   'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
    high:       'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
    at_risk:    'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
    warning:    'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
    normal:     'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700',
    inactive:   'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700',
    light:      'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30',
    ready:      'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30',
  };
  const cls = map[level] ?? 'bg-muted text-muted-foreground border-border';
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-semibold uppercase tracking-[0.05em] ${cls}`}>
      {level.replace(/_/g, ' ')}
    </span>
  );
}

// ─── Readiness bar ────────────────────────────────────────────────────────────

function ReadinessBar({ score }: { score: number }) {
  const colour = score >= 100 ? 'bg-emerald-500' : score >= 75 ? 'bg-amber-400' : score >= 50 ? 'bg-orange-500' : 'bg-red-500';
  const text   = score >= 100 ? 'text-emerald-700 dark:text-emerald-300' : score >= 75 ? 'text-amber-700 dark:text-amber-300' : 'text-red-700 dark:text-red-300';
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-2 w-20 overflow-hidden rounded-full bg-muted">
        <div className={`h-2 rounded-full ${colour}`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-semibold ${text}`}>{score}%</span>
    </div>
  );
}

// ─── Dense table ──────────────────────────────────────────────────────────────

function Tbl({ headers, children }: { headers: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/40">
            {headers.map((h) => (
              <th key={h} className={`${LABEL_CLS} whitespace-nowrap px-4 py-3 text-left`}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">{children}</tbody>
      </table>
    </div>
  );
}

function Tr({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <tr className={`bg-card ${onClick ? 'cursor-pointer hover:bg-muted/30' : ''} transition-colors`} onClick={onClick}>
      {children}
    </tr>
  );
}

function Td({ children, mono, muted, right, bold }: {
  children: React.ReactNode; mono?: boolean; muted?: boolean; right?: boolean; bold?: boolean;
}) {
  return (
    <td className={`whitespace-nowrap px-4 py-3 ${mono ? '' : ''} ${muted ? 'text-muted-foreground' : 'text-foreground'} ${right ? 'text-right' : ''} ${bold ? 'font-semibold' : ''}`}>
      {children}
    </td>
  );
}

function Empty({ msg }: { msg: string }) {
  return <tr><td colSpan={20} className="px-4 py-12 text-center text-sm text-muted-foreground">{msg}</td></tr>;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">{children}</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

// ─── Alert ribbon ─────────────────────────────────────────────────────────────

interface Alert { level: 'critical' | 'warning' | 'info'; key: string; message: string; count: number }

function AlertRibbon({ alerts }: { alerts: Alert[] }) {
  if (!alerts.length) return null;
  return (
    <div className="mb-5 space-y-1.5">
      {alerts.filter((a) => a.level === 'critical').map((a) => (
        <div key={a.key} className="flex items-center gap-3 rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-4 py-3">
          <XCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" weight="fill" />
          <span className="text-sm font-medium text-red-800 dark:text-red-300">{a.message}</span>
          <span className="ml-auto rounded-full bg-red-600 px-2.5 py-0.5 text-xs font-bold text-white">{a.count}</span>
        </div>
      ))}
      {alerts.filter((a) => a.level === 'warning').map((a) => (
        <div key={a.key} className="flex items-center gap-3 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3">
          <Warning className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
          <span className="text-sm font-medium text-amber-800 dark:text-amber-300">{a.message}</span>
          <span className="ml-auto rounded-full bg-amber-500 px-2.5 py-0.5 text-xs font-bold text-white">{a.count}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Recharts shared styles ───────────────────────────────────────────────────

const CHART_COLORS = { billed: '#2563eb', collected: '#10b981', outstanding: '#f59e0b' };

function ChartTooltipGbp({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-lg text-xs">
      <div className="mb-1.5 font-semibold text-foreground">{label}</div>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground capitalize">{p.name}:</span>
          <span className="font-semibold">{gbp(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Tab: Overview ────────────────────────────────────────────────────────────

function OverviewTab() {
  const from = twelveMonthsAgo();
  const to   = today();
  const opsQ  = useQuery({ queryKey: ['rpt-ops'],        queryFn: fetchClerkOperations,                 staleTime: 60_000 });
  const workQ = useQuery({ queryKey: ['rpt-work'],        queryFn: fetchBarristerWorkload,               staleTime: 120_000 });
  const hlthQ = useQuery({ queryKey: ['rpt-hlth'],        queryFn: () => fetchMatterHealth(21),          staleTime: 120_000 });
  const feesQ = useQuery({ queryKey: ['rpt-of'],          queryFn: fetchOutstandingFees,                 staleTime: 120_000 });
  const finQ  = useQuery({ queryKey: ['rpt-fin-ov', from, to], queryFn: () => fetchFinancialPerformance(from, to), staleTime: 300_000 });

  const ops      = opsQ.data;
  const workload = workQ.data ?? [];
  const matters  = hlthQ.data ?? [];
  const fees     = feesQ.data ?? [];
  const fin      = finQ.data;

  const totalOut     = fees.reduce((s, r) => s + r.total, 0);
  const d90Total     = fees.reduce((s, r) => s + r.d90, 0);
  const overloaded   = workload.filter((b) => b.workloadStatus === 'overloaded').length;
  const stalled      = matters.filter((m) => m.healthFlag === 'stalled').length;
  const atRisk       = matters.filter((m) => m.healthFlag === 'at_risk').length;

  if (opsQ.isLoading && finQ.isLoading) return <Loading />;

  // Prepare chart data — last 6 months from monthly breakdown
  const chartData = (fin?.monthly ?? []).slice(-6).map((m) => ({
    month: m.month.slice(5), // "YYYY-MM" → "MM"
    Billed: m.billed,
    Collected: m.collected,
  }));

  return (
    <div className="space-y-8">

      {/* Financial KPIs */}
      <div>
        <SectionTitle>Financial position — last 12 months</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="Billed"        value={fin ? gbp(fin.summary.totalBilled) : '—'}     accent="blue" />
          <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="Collected"     value={fin ? gbp(fin.summary.totalCollected) : '—'}  accent="green"
            sub={fin ? `${fin.summary.collectionRate.toFixed(1)}% collection rate` : undefined} />
          <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="Outstanding"   value={feesQ.isError ? '—' : gbp(totalOut)}
            accent={totalOut > 50_000 ? 'amber' : totalOut > 150_000 ? 'red' : 'default'} />
          <KpiCard icon={<Warning className="h-5 w-5" />}     label="Aged 90d+"     value={feesQ.isError ? '—' : gbp(d90Total)}
            accent={d90Total > 0 ? 'red' : 'default'} sub={feesQ.isError ? "Couldn't load" : d90Total > 0 ? 'Immediate action required' : 'No aged debt'} />
        </div>
      </div>

      {/* Revenue trend chart */}
      {chartData.length > 0 && (
        <div>
          <SectionTitle>Revenue trend — last 6 months</SectionTitle>
          <div className="rounded-xl border border-border bg-card p-5">
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 4, right: 4, bottom: 0, left: 8 }}>
                <defs>
                  <linearGradient id="billedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={CHART_COLORS.billed}    stopOpacity={0.15} />
                    <stop offset="95%" stopColor={CHART_COLORS.billed}    stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="collectedGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={CHART_COLORS.collected} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={CHART_COLORS.collected} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={gbpK} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={48} />
                <Tooltip content={<ChartTooltipGbp />} />
                <Area type="monotone" dataKey="Billed"    stroke={CHART_COLORS.billed}    strokeWidth={2} fill="url(#billedGrad)"    dot={false} />
                <Area type="monotone" dataKey="Collected" stroke={CHART_COLORS.collected} strokeWidth={2} fill="url(#collectedGrad)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
            <div className="mt-3 flex items-center gap-5 border-t border-border pt-3">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2.5 w-5 rounded-sm" style={{ background: CHART_COLORS.billed }} />Billed</span>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2.5 w-5 rounded-sm" style={{ background: CHART_COLORS.collected }} />Collected</span>
            </div>
          </div>
        </div>
      )}

      {/* Operational KPIs */}
      <div>
        <SectionTitle>Operational status</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard icon={<Briefcase className="h-5 w-5" />}    label="Pending allocations"  value={ops?.pendingAllocations ?? '—'}  accent={(ops?.pendingAllocations ?? 0) > 3 ? 'amber' : 'default'} />
          <KpiCard icon={<ClipboardText className="h-5 w-5" />} label="Unassigned matters"   value={ops?.unassignedMatters ?? '—'}   accent={(ops?.unassignedMatters ?? 0) > 0 ? 'amber' : 'default'} />
          <KpiCard icon={<Users className="h-5 w-5" />}         label="Pending conferences"  value={ops?.pendingConferences ?? '—'}  accent={(ops?.pendingConferences ?? 0) > 2 ? 'amber' : 'default'} />
          <KpiCard icon={<Gavel className="h-5 w-5" />}         label="Hearings next 7 days" value={ops?.upcomingHearings.length ?? '—'} accent="blue" />
        </div>
      </div>

      {/* Barrister capacity + matter health */}
      {/* `[&>*]:min-w-0` is what keeps this on the screen at phone width. A grid item's
          `min-width` is `auto`, which means a column sized `auto` — the single column this
          becomes below `lg` — grows to the item's MINIMUM CONTENT width rather than to the
          screen. The capacity chart and the flagged-cases table came to 818px between them, so
          the column was 818px inside a 355px page: the right half of the chart and the whole of
          Days idle / Tasks / Flag were past the right edge, and the shell CLIPS that (see
          AppShell.tsx), so they were not merely awkward — they were gone, with the table's own
          `overflow-x-auto` useless because the scroller itself ran off the screen. Let the items
          shrink and the column is the width of the page: the chart resizes to fit and the table
          scrolls inside its box. */}
      <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-2">

        {/* Barrister workload chart */}
        <div>
          <SectionTitle>Barrister capacity</SectionTitle>
          {workQ.isLoading ? <Loading /> : workQ.isError ? (
            <QueryErrorState compact what="barrister capacity" onRetry={() => workQ.refetch()} />
          ) : workload.length === 0 ? (
            <p className="text-sm text-muted-foreground">No barrister data available.</p>
          ) : (
            <div className="rounded-xl border border-border bg-card p-4">
              <ResponsiveContainer width="100%" height={Math.max(120, workload.length * 36)}>
                <BarChart data={workload.slice(0, 8).map((b) => ({
                  name: `${b.firstName[0]}. ${b.lastName}${b.memberType === 'silk' ? ' KC' : ''}`,
                  Matters: b.activeMatters,
                  status: b.workloadStatus,
                }))} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false} width={96} />
                  <Tooltip formatter={(v) => [`${v} matters`, 'Active matters']} contentStyle={{ fontSize: 12, background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' }} />
                  <Bar dataKey="Matters" radius={[0, 4, 4, 0]} maxBarSize={20}>
                    {workload.slice(0, 8).map((b) => (
                      <Cell key={b.barristerId}
                        fill={b.workloadStatus === 'overloaded' ? '#ef4444' : b.workloadStatus === 'high' ? '#f59e0b' : '#2563eb'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-3 flex items-center gap-4 border-t border-border pt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-sm bg-red-500" />Overloaded</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-sm bg-amber-400" />High</span>
                <span className="flex items-center gap-1.5"><span className="h-2.5 w-4 rounded-sm bg-brand-600" />Normal</span>
                {overloaded > 0 && <span className="ml-auto font-semibold text-red-600 dark:text-red-400">⚠ {overloaded} overloaded</span>}
              </div>
            </div>
          )}
        </div>

        {/* Matter health */}
        <div>
          <SectionTitle>Matter health — flagged cases</SectionTitle>
          {hlthQ.isLoading ? <Loading /> : hlthQ.isError ? (
            // Not "All matters current — no flags raised".
            <QueryErrorState compact what="matter health" onRetry={() => hlthQ.refetch()} />
          ) : (
            <>
              <div className="mb-3 flex items-center gap-3">
                {stalled > 0 && <span className="rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-700 dark:text-red-300">{stalled} stalled</span>}
                {atRisk > 0  && <span className="rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">{atRisk} at risk</span>}
                {stalled === 0 && atRisk === 0 && <span className="text-sm text-emerald-600 dark:text-emerald-400">All matters current — no flags raised.</span>}
              </div>
              {(stalled > 0 || atRisk > 0) && (
                <Tbl headers={['Matter', 'Days idle', 'Tasks', 'Flag']}>
                  {matters.filter((m) => m.healthFlag !== 'inactive').slice(0, 6).map((m) => (
                    <Tr key={m.caseId}>
                      <Td>
                        <div className="font-medium">{m.caseName}</div>
                        <div className="text-xs text-muted-foreground">{m.caseNumber}</div>
                      </Td>
                      <Td mono muted>
                        <span className={m.daysSinceUpdate >= 30 ? 'font-semibold text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}>
                          {m.daysSinceUpdate}d
                        </span>
                      </Td>
                      <Td mono muted>
                        {m.overdueTasks > 0 ? <span className="text-amber-600 dark:text-amber-400">{m.overdueTasks}</span> : '—'}
                      </Td>
                      <Td><Chip level={m.healthFlag} /></Td>
                    </Tr>
                  ))}
                </Tbl>
              )}
            </>
          )}
        </div>
      </div>

      {/* Court diary */}
      {ops && ops.upcomingHearings.length > 0 && (
        <div>
          <SectionTitle>Court diary — next 7 days</SectionTitle>
          <Tbl headers={['Date / Time', 'Type', 'Matter', 'Court', 'Barrister']}>
            {ops.upcomingHearings.map((h) => (
              <Tr key={h.id}>
                <Td mono muted>{fmtDateTime(h.hearingDate)}</Td>
                <Td muted>{htLabel(h.hearingType)}</Td>
                <Td>
                  <div className="font-medium">{h.case.caseName}</div>
                  <div className="text-xs text-muted-foreground">{h.case.caseNumber}</div>
                </Td>
                <Td muted>{h.court?.shortName ?? '—'}</Td>
                <Td muted>{barName(h.case.leadBarrister)}</Td>
              </Tr>
            ))}
          </Tbl>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Hearing Readiness ───────────────────────────────────────────────────

function HearingReadinessTab() {
  const [days, setDays] = useState(7);
  const { data = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['rpt-readiness', days],
    queryFn: () => fetchHearingReadiness(days),
    staleTime: 60_000,
  });
  const urgent = data.filter((h) => h.readinessScore < 75);
  const ready  = data.filter((h) => h.readinessScore === 100);
  const exportRows = () => downloadCSV(`hearing-readiness-${today()}.csv`,
    data.map((h) => ({ Hearing: fmtDateTime(h.hearingDate), Type: htLabel(h.hearingType), Matter: h.caseName, Ref: h.caseNumber, Court: h.court?.shortName ?? '', Barrister: barName(h.leadBarrister), Score: h.readinessScore, Issues: h.flags.join(' | ') })));

  return (
    <div className="space-y-5">

      {/* Summary KPIs */}
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard icon={<Gavel className="h-5 w-5" />}   label="Hearings in window" value={isError ? '—' : data.length}  accent="blue" />
        <KpiCard icon={<Warning className="h-5 w-5" />}  label="Require attention"  value={isError ? '—' : urgent.length} accent={urgent.length > 0 ? 'red' : 'default'} />
        <KpiCard icon={<Gavel className="h-5 w-5" />}   label="Fully prepared"     value={isError ? '—' : ready.length}  accent={ready.length > 0 ? 'green' : 'default'} />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {[3, 7, 14].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${days === d ? 'border-brand-600 bg-brand-600 text-white' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
              {d} days
            </button>
          ))}
        </div>
        <ExportBtn onClick={exportRows} />
      </div>

      {isLoading ? <Loading /> : isError ? (
        <QueryErrorState what="hearing readiness" onRetry={() => refetch()} />
      ) : (
        <Tbl headers={['Date / Time', 'Type', 'Matter', 'Court', 'Barrister', 'Readiness', 'Issues']}>
          {data.length === 0 && <Empty msg={`No hearings scheduled within ${days} days.`} />}
          {data.map((h) => (
            <Tr key={h.id}>
              <Td mono muted>{fmtDateTime(h.hearingDate)}</Td>
              <Td muted>{htLabel(h.hearingType)}</Td>
              <Td>
                <div className="font-medium">{h.caseName}</div>
                <div className="text-xs text-muted-foreground">{h.caseNumber}</div>
              </Td>
              <Td muted>{h.court?.shortName ?? '—'}</Td>
              <Td muted>{barName(h.leadBarrister)}</Td>
              <Td><ReadinessBar score={h.readinessScore} /></Td>
              <Td>
                {h.flags.length === 0 ? (
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Ready</span>
                ) : (
                  <div className="space-y-0.5">
                    {h.flags.map((f) => (
                      <div key={f} className="text-xs text-amber-700 dark:text-amber-300">
                        {f === 'no_bundle' ? '↳ No documents' : f === 'overdue_tasks' ? '↳ Tasks overdue' : f === 'pending_conference' ? '↳ Conference pending' : '↳ No video link'}
                      </div>
                    ))}
                  </div>
                )}
              </Td>
            </Tr>
          ))}
        </Tbl>
      )}
    </div>
  );
}

// ─── Tab: Fees & Revenue ──────────────────────────────────────────────────────

function FeesTab() {
  const [from, setFrom] = useState(twelveMonthsAgo);
  const [to, setTo]     = useState(today);
  const feesQ = useQuery({ queryKey: ['rpt-fees'],               queryFn: fetchOutstandingFees,                          staleTime: 120_000 });
  const finQ  = useQuery({ queryKey: ['rpt-financial', from, to], queryFn: () => fetchFinancialPerformance(from, to),    staleTime: 300_000 });
  const fees = feesQ.data ?? [];
  const fin  = finQ.data;
  const totalOut = fees.reduce((s, r) => s + r.total, 0);
  const d90Total = fees.reduce((s, r) => s + r.d90, 0);
  const exportFees = () => downloadCSV(`outstanding-fees-${today()}.csv`,
    fees.map((r) => ({ Client: r.clientName, Matters: r.mattersCount, Current: r.current, '30d': r.d30, '60d': r.d60, '90d+': r.d90, Total: r.total })));
  const exportFin = () => downloadCSV(`financial-${from}-${to}.csv`,
    (fin?.monthly ?? []).map((m) => ({ Month: m.month, Billed: m.billed, Collected: m.collected, Outstanding: m.billed - m.collected })));

  const barData = (fin?.monthly ?? []).slice(-8).map((m) => ({
    month: m.month.slice(5),
    Billed: m.billed, Collected: m.collected,
  }));

  return (
    <div className="space-y-8">

      {/* Outstanding fees */}
      <div>
        <div className="mb-5 flex items-center justify-between">
          <SectionTitle>Outstanding fees — aged debt analysis</SectionTitle>
          <ExportBtn onClick={exportFees} />
        </div>
        <div className="mb-5 grid gap-4 sm:grid-cols-4">
          <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="Total outstanding" value={feesQ.isError ? '—' : gbp(totalOut)} accent={totalOut > 100_000 ? 'red' : totalOut > 50_000 ? 'amber' : 'default'} />
          <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="Current (0–30d)" value={feesQ.isError ? '—' : gbp(fees.reduce((s, r) => s + r.current + r.d30, 0))} accent="blue" />
          <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="31–60 days" value={feesQ.isError ? '—' : gbp(fees.reduce((s, r) => s + r.d60, 0))} accent={fees.reduce((s, r) => s + r.d60, 0) > 10_000 ? 'amber' : 'default'} />
          <KpiCard icon={<Warning className="h-5 w-5" />}     label="90d+ exposure" value={feesQ.isError ? '—' : gbp(d90Total)} accent={d90Total > 0 ? 'red' : 'default'} sub={feesQ.isError ? "Couldn't load" : d90Total > 0 ? 'Immediate action' : 'No aged debt'} />
        </div>
        {feesQ.isLoading ? <Loading /> : feesQ.isError ? (
          <QueryErrorState what="outstanding fees" onRetry={() => feesQ.refetch()} />
        ) : (
          <Tbl headers={['Instructing solicitor / client', 'Matters', 'Current', '30d', '60d', '90d+', 'Total']}>
            {fees.length === 0 && <Empty msg="No outstanding fees." />}
            {fees.map((r) => (
              <Tr key={r.contactId ?? r.clientName}>
                <Td bold>{r.clientName}</Td>
                <Td mono muted>{r.mattersCount}</Td>
                <Td mono>{r.current > 0 ? gbp(r.current) : <span className="text-muted-foreground">—</span>}</Td>
                <Td mono>{r.d30 > 0 ? <span className="text-amber-700 dark:text-amber-300">{gbp(r.d30)}</span> : <span className="text-muted-foreground">—</span>}</Td>
                <Td mono>{r.d60 > 0 ? <span className="text-orange-600 dark:text-orange-400">{gbp(r.d60)}</span> : <span className="text-muted-foreground">—</span>}</Td>
                <Td mono>{r.d90 > 0 ? <span className="font-bold text-red-600 dark:text-red-400">{gbp(r.d90)}</span> : <span className="text-muted-foreground">—</span>}</Td>
                <Td mono bold right>{gbp(r.total)}</Td>
              </Tr>
            ))}
          </Tbl>
        )}
      </div>

      {/* Financial performance */}
      <div>
        <div className="mb-5 flex items-center justify-between">
          <SectionTitle>Financial performance</SectionTitle>
          <div className="flex items-center gap-2">
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
              className="h-8 rounded-lg border border-border bg-card px-3 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400" />
            <span className="text-xs text-muted-foreground">to</span>
            <input type="date" value={to} min={from} max={today()} onChange={(e) => setTo(e.target.value)}
              className="h-8 rounded-lg border border-border bg-card px-3 text-xs focus:outline-none focus:ring-2 focus:ring-brand-400" />
            <ExportBtn onClick={exportFin} />
          </div>
        </div>

        {fin && (
          <div className="mb-5 grid gap-4 sm:grid-cols-4">
            <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="Total billed"      value={gbp(fin.summary.totalBilled)} accent="blue" />
            <KpiCard icon={<CurrencyGbp className="h-5 w-5" />} label="Total collected"   value={gbp(fin.summary.totalCollected)} accent="green" />
            <KpiCard icon={<TrendUp className="h-5 w-5" />}     label="Collection rate"   value={`${fin.summary.collectionRate.toFixed(1)}%`} accent={fin.summary.collectionRate < 80 ? 'amber' : 'green'} />
            <KpiCard icon={<ClipboardText className="h-5 w-5" />} label="Fee notes issued" value={fin.summary.feeNoteCount} />
          </div>
        )}

        {/* Monthly bar chart */}
        {barData.length > 0 && (
          <div className="mb-5 rounded-xl border border-border bg-card p-5">
            <div className={`${LABEL_CLS} mb-4`}>Monthly billed vs collected</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={barData} margin={{ top: 0, right: 4, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={gbpK} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<ChartTooltipGbp />} />
                <Bar dataKey="Billed"    fill={CHART_COLORS.billed}    radius={[3, 3, 0, 0]} maxBarSize={24} />
                <Bar dataKey="Collected" fill={CHART_COLORS.collected} radius={[3, 3, 0, 0]} maxBarSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {finQ.isLoading ? <Loading /> : finQ.isError ? (
          <QueryErrorState what="financial performance" onRetry={() => finQ.refetch()} />
        ) : fin && (
          /* `[&>*]:min-w-0` for the same reason as the capacity grid above: without it the
             single phone-width column is sized by the widest table inside it. */
          <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-2">
            <div>
              <div className={`${LABEL_CLS} mb-3`}>Monthly breakdown</div>
              <Tbl headers={['Month', 'Billed', 'Collected', 'Outstanding', 'Rate']}>
                {fin.monthly.length === 0 && <Empty msg="No data for this period." />}
                {fin.monthly.map((m) => {
                  const outstanding = m.billed - m.collected;
                  const rate = m.billed > 0 ? (m.collected / m.billed) * 100 : 0;
                  return (
                    <Tr key={m.month}>
                      <Td mono muted>{m.month}</Td>
                      <Td mono>{gbp(m.billed)}</Td>
                      <Td mono>{gbp(m.collected)}</Td>
                      <Td mono>{outstanding > 0 ? <span className="text-amber-700 dark:text-amber-300">{gbp(outstanding)}</span> : <span className="text-emerald-600 dark:text-emerald-400">—</span>}</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                            <div className={`h-1.5 rounded-full ${rate >= 90 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-400' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(100, rate).toFixed(0)}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground">{rate.toFixed(0)}%</span>
                        </div>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbl>
            </div>
            <div>
              <div className={`${LABEL_CLS} mb-3`}>By practice area</div>
              <Tbl headers={['Practice area', 'Billed', 'Collected', 'Rate', 'Matters']}>
                {fin.byPracticeArea.length === 0 && <Empty msg="No practice area data." />}
                {fin.byPracticeArea.map((p) => {
                  const rate = p.billed > 0 ? (p.collected / p.billed) * 100 : 0;
                  return (
                    <Tr key={p.practiceArea}>
                      <Td bold>{p.practiceArea}</Td>
                      <Td mono>{gbp(p.billed)}</Td>
                      <Td mono>{gbp(p.collected)}</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                            <div className={`h-1.5 rounded-full ${rate >= 90 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-400' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(100, rate).toFixed(0)}%` }} />
                          </div>
                          <span className="text-xs text-muted-foreground">{rate.toFixed(0)}%</span>
                        </div>
                      </Td>
                      <Td mono muted>{p.count}</Td>
                    </Tr>
                  );
                })}
              </Tbl>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Barrister Utilization ───────────────────────────────────────────────

function BarristersTab() {
  const { data = [], isLoading, isError, refetch } = useQuery({ queryKey: ['rpt-workload'], queryFn: fetchBarristerWorkload, staleTime: 120_000 });
  const exportRows = () => downloadCSV(`barrister-utilization-${today()}.csv`,
    data.map((b) => ({ Name: `${b.firstName} ${b.lastName}${b.memberType === 'silk' ? ' KC' : ''}`, Rank: b.memberType, 'Active matters': b.activeMatters, 'Hearings 7d': b.hearings7d, 'Hearings 30d': b.hearings30d, Status: b.workloadStatus })));
  const overloaded = data.filter((b) => b.workloadStatus === 'overloaded');
  const high = data.filter((b) => b.workloadStatus === 'high');
  const maxM = Math.max(...data.map((b) => b.activeMatters), 1);

  const chartData = data.map((b) => ({
    name: `${b.firstName[0]}. ${b.lastName}${b.memberType === 'silk' ? ' KC' : ''}`,
    matters: b.activeMatters,
    h7: b.hearings7d,
    status: b.workloadStatus,
  }));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard icon={<Users className="h-5 w-5" />} label="Total barristers"  value={isError ? '—' : data.length} accent="blue" />
        <KpiCard icon={<Warning className="h-5 w-5" />} label="Overloaded"       value={isError ? '—' : overloaded.length} accent={overloaded.length > 0 ? 'red' : 'default'} sub={overloaded.length > 0 ? overloaded.map((b) => `${b.firstName} ${b.lastName}`).join(', ') : 'None overloaded'} />
        <KpiCard icon={<Warning className="h-5 w-5" />} label="High load"        value={isError ? '—' : high.length} accent={high.length > 0 ? 'amber' : 'default'} />
      </div>

      {/* Chart */}
      {!isLoading && chartData.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className={`${LABEL_CLS} mb-4`}>Active matters per barrister</div>
          <ResponsiveContainer width="100%" height={Math.max(140, chartData.length * 40)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 16, left: 8, bottom: 0 }}>
              <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} width={110} />
              <Tooltip formatter={(v) => [`${v} matters`, '']} contentStyle={{ fontSize: 12, background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' }} />
              <Bar dataKey="matters" radius={[0, 4, 4, 0]} maxBarSize={22}>
                {chartData.map((d) => (
                  <Cell key={d.name}
                    fill={d.status === 'overloaded' ? '#ef4444' : d.status === 'high' ? '#f59e0b' : '#2563eb'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex justify-end">
        <ExportBtn onClick={exportRows} />
      </div>

      {isLoading ? <Loading /> : isError ? (
        <QueryErrorState what="barrister utilisation" onRetry={() => refetch()} />
      ) : (
        <Tbl headers={['Barrister', 'Rank', 'Active matters', 'Load', 'Hearings 7d', 'Hearings 30d', 'Status']}>
          {data.length === 0 && <Empty msg="No barrister data." />}
          {data.map((b) => (
            <Tr key={b.barristerId}>
              <Td bold>{b.firstName} {b.lastName}{b.memberType === 'silk' ? ' KC' : ''}</Td>
              <Td muted>{b.memberType === 'silk' ? "King's Counsel" : b.memberType === 'junior' ? 'Junior' : b.memberType}</Td>
              <Td mono>{b.activeMatters}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                    <div className={`h-2 rounded-full ${b.workloadStatus === 'overloaded' ? 'bg-red-500' : b.workloadStatus === 'high' ? 'bg-amber-400' : 'bg-brand-500'}`}
                      style={{ width: `${Math.min(100, Math.round((b.activeMatters / maxM) * 100))}%` }} />
                  </div>
                </div>
              </Td>
              <Td mono>{b.hearings7d}</Td>
              <Td mono>{b.hearings30d}</Td>
              <Td><Chip level={b.workloadStatus} /></Td>
            </Tr>
          ))}
        </Tbl>
      )}
    </div>
  );
}

// ─── Tab: Matter Health ───────────────────────────────────────────────────────

function MattersTab() {
  const [threshold, setThreshold] = useState(21);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';
  const { data = [], isLoading, isError, refetch } = useQuery({ queryKey: ['rpt-health', threshold], queryFn: () => fetchMatterHealth(threshold), staleTime: 120_000 });
  const stalled = data.filter((m) => m.healthFlag === 'stalled');
  const atRisk  = data.filter((m) => m.healthFlag === 'at_risk');
  const exportRows = () => downloadCSV(`matter-health-${today()}.csv`,
    data.map((m) => ({ Matter: m.caseName, Ref: m.caseNumber, Status: m.status, 'Practice area': m.practiceArea ?? '', Barrister: barName(m.leadBarrister), 'Days idle': m.daysSinceUpdate, 'Overdue tasks': m.overdueTasks, Flag: m.healthFlag })));
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard icon={<Briefcase className="h-5 w-5" />} label="Stalled matters" value={isError ? '—' : stalled.length} accent={stalled.length > 0 ? 'red' : 'default'} sub="30+ days no activity" />
        <KpiCard icon={<Warning className="h-5 w-5" />}   label="At risk"         value={isError ? '—' : atRisk.length}  accent={atRisk.length > 0 ? 'amber' : 'default'} sub="21+ days no activity" />
        <KpiCard icon={<Briefcase className="h-5 w-5" />} label="Total flagged"   value={isError ? '—' : data.length}    accent="blue" />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {[14, 21, 30].map((d) => (
            <button key={d} onClick={() => setThreshold(d)}
              className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${threshold === d ? 'border-brand-600 bg-brand-600 text-white' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
              {d}d inactive
            </button>
          ))}
        </div>
        <ExportBtn onClick={exportRows} />
      </div>
      {isLoading ? <Loading /> : isError ? (
        <QueryErrorState what="matter health" onRetry={() => refetch()} />
      ) : (
        <Tbl headers={['Matter', 'Barrister', 'Practice area', 'Days idle', 'Overdue tasks', 'Upcoming hearings', 'Flag']}>
          {data.length === 0 && <Empty msg={`No matters inactive for ${threshold}+ days.`} />}
          {data.map((m) => (
            <Tr key={m.caseId} onClick={() => navigate(`${basePath}/matters/${m.caseId}`)}>
              <Td>
                <div className="font-semibold">{m.caseName}</div>
                <div className="text-xs text-muted-foreground">{m.caseNumber}</div>
              </Td>
              <Td muted>{barName(m.leadBarrister)}</Td>
              <Td muted>{m.practiceArea ?? '—'}</Td>
              <Td mono>
                <span className={m.daysSinceUpdate >= 30 ? 'font-bold text-red-600 dark:text-red-400' : 'font-semibold text-amber-600 dark:text-amber-400'}>
                  {m.daysSinceUpdate}d
                </span>
              </Td>
              <Td mono>{m.overdueTasks > 0 ? <span className="font-semibold text-amber-600 dark:text-amber-400">{m.overdueTasks}</span> : <span className="text-muted-foreground">—</span>}</Td>
              <Td muted>{m.upcomingHearings > 0 ? `${m.upcomingHearings} scheduled` : '—'}</Td>
              <Td><Chip level={m.healthFlag} /></Td>
            </Tr>
          ))}
        </Tbl>
      )}
    </div>
  );
}

// ─── Tab: Operations ──────────────────────────────────────────────────────────

function OperationsTab() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['rpt-clerk-ops'], queryFn: fetchClerkOperations, staleTime: 60_000 });
  if (isLoading) return <Loading />;
  // Otherwise every counter below falls back to 0 and the court diary says "No hearings".
  if (isError) return <QueryErrorState what="operations" onRetry={() => refetch()} />;
  const hearings = data?.upcomingHearings ?? [];
  return (
    <div className="space-y-6">
      <div>
        <SectionTitle>Pending operational actions</SectionTitle>
        <div className="grid gap-4 sm:grid-cols-3">
          <KpiCard icon={<Briefcase className="h-5 w-5" />}    label="Brief offers awaiting" value={data?.pendingAllocations ?? 0} accent={(data?.pendingAllocations ?? 0) > 2 ? 'amber' : 'default'} sub="Pending with barristers" />
          <KpiCard icon={<ClipboardText className="h-5 w-5" />} label="Unassigned matters"    value={data?.unassignedMatters ?? 0}  accent={(data?.unassignedMatters ?? 0) > 0 ? 'amber' : 'default'} sub="No lead barrister" />
          <KpiCard icon={<Users className="h-5 w-5" />}         label="Conferences to book"   value={data?.pendingConferences ?? 0} accent={(data?.pendingConferences ?? 0) > 1 ? 'amber' : 'default'} sub="Awaiting confirmed slot" />
        </div>
      </div>
      <div>
        <SectionTitle>Court diary — next 7 days</SectionTitle>
        <Tbl headers={['Date', 'Time', 'Type', 'Matter', 'Court', 'Barrister', '']}>
          {hearings.length === 0 && <Empty msg="No hearings in the next 7 days." />}
          {hearings.map((h) => {
            const d = new Date(h.hearingDate);
            return (
              <Tr key={h.id} onClick={() => navigate(`${basePath}/hearings/${h.id}`)}>
                <Td mono muted>{fmtDate(h.hearingDate)}</Td>
                <Td mono muted>{d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</Td>
                <Td muted>{htLabel(h.hearingType)}</Td>
                <Td>
                  <div className="font-semibold">{h.case.caseName}</div>
                  <div className="text-xs text-muted-foreground">{h.case.caseNumber}</div>
                </Td>
                <Td muted>{h.court?.shortName ?? '—'}</Td>
                <Td muted>{barName(h.case.leadBarrister)}</Td>
                <Td><ArrowRight className="h-4 w-4 text-muted-foreground/50" /></Td>
              </Tr>
            );
          })}
        </Tbl>
      </div>
    </div>
  );
}

// ─── Tab: Recovery Queue ─────────────────────────────────────────────────────

function RecoveryTab() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';

  const overdueQ = useQuery({
    queryKey: ['rpt-overdue-notes'],
    queryFn: () => fetchFeeNotes({ status: 'overdue', limit: 100 }),
    staleTime: 120_000,
  });
  const notes: FeeNoteListRow[] = overdueQ.data?.feeNotes ?? [];

  const totalOverdue = notes.reduce((s, n) => s + n.outstanding, 0); // server-authoritative
  const oldestDue = notes.length > 0
    ? Math.max(...notes.map((n) => Math.ceil((Date.now() - new Date(n.dueDate).getTime()) / 86_400_000)))
    : 0;

  const sorted = [...notes].sort((a, b) => b.outstanding - a.outstanding); // highest balance first

  const exportRows = () => downloadCSV(`recovery-queue-${today()}.csv`,
    sorted.map((n) => ({
      'Fee note': n.feeNoteNumber,
      Matter: n.case ? `${n.case.caseNumber} ${n.case.caseName}` : '—',
      'Due date': new Date(n.dueDate).toLocaleDateString('en-GB'),
      'Days overdue': Math.ceil((Date.now() - new Date(n.dueDate).getTime()) / 86_400_000),
      'Total (£)': Number(n.total).toFixed(2),
      'Paid (£)': Number(n.amountPaid).toFixed(2),
      'Balance (£)': n.outstanding.toFixed(2),
      Status: FEE_STATUS_LABELS[n.status] ?? n.status,
    })));

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <KpiCard icon={<Warning className="h-5 w-5" />}      label="Overdue notes"    value={overdueQ.isError ? '—' : notes.length}  accent={notes.length > 0 ? 'red' : 'default'} sub="Status: overdue" />
        <KpiCard icon={<CurrencyGbp className="h-5 w-5" />}  label="Total outstanding" value={overdueQ.isError ? '—' : gbp(totalOverdue)} accent={totalOverdue > 0 ? 'red' : 'default'} />
        <KpiCard icon={<Warning className="h-5 w-5" />}      label="Most overdue"      value={oldestDue > 0 ? `${oldestDue}d` : '—'} accent={oldestDue > 60 ? 'red' : oldestDue > 30 ? 'amber' : 'default'} />
      </div>

      <div className="flex items-center justify-between">
        <SectionTitle>Overdue fee notes — sorted by outstanding balance</SectionTitle>
        <ExportBtn onClick={exportRows} />
      </div>

      {overdueQ.isLoading ? <Loading /> : overdueQ.isError ? (
        // Not "No overdue fee notes — collection is current".
        <QueryErrorState what="the recovery queue" onRetry={() => overdueQ.refetch()} />
      ) : (
        <Tbl headers={['Fee note', 'Matter', 'Due date', 'Days overdue', 'Balance', 'Actions']}>
          {notes.length === 0 && <Empty msg="No overdue fee notes — collection is current." />}
          {sorted.map((n) => {
            const balance     = n.outstanding;
            const daysOverdue = Math.ceil((Date.now() - new Date(n.dueDate).getTime()) / 86_400_000);
            const urgent = daysOverdue > 60;
            return (
              <Tr key={n.id} onClick={() => n.case && navigate(`${basePath}/matters/${n.case.id}?tab=fees`)}>
                <Td bold><span className={urgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}>{n.feeNoteNumber}</span></Td>
                <Td>
                  {n.case ? (
                    <>
                      <div className="font-semibold">{n.case.caseName}</div>
                      <div className="text-xs text-muted-foreground">{n.case.caseNumber}</div>
                    </>
                  ) : '—'}
                </Td>
                <Td mono muted>{new Date(n.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })}</Td>
                <Td mono>
                  <span className={urgent ? 'font-bold text-red-600 dark:text-red-400' : 'font-semibold text-amber-600 dark:text-amber-400'}>
                    {daysOverdue}d
                  </span>
                </Td>
                <Td mono bold right>
                  <span className={urgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}>{gbp(balance)}</span>
                </Td>
                <Td>
                  <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {n.case && (
                      <button
                        type="button"
                        onClick={() => navigate(`${basePath}/matters/${n.case!.id}?tab=messages`)}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground transition hover:bg-muted"
                        title="Open matter communications"
                      >
                        <EnvelopeSimple className="h-3 w-3" /> Chase
                      </button>
                    )}
                    {n.case && (
                      <button
                        type="button"
                        onClick={() => navigate(`${basePath}/matters/${n.case!.id}?tab=fees`)}
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-xs font-medium text-foreground transition hover:bg-muted"
                      >
                        <ArrowRight className="h-3 w-3" /> Fees
                      </button>
                    )}
                  </div>
                </Td>
              </Tr>
            );
          })}
        </Tbl>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Tab = 'overview' | 'hearings' | 'fees' | 'recovery' | 'barristers' | 'matters' | 'operations';

const TABS: { id: Tab; label: string; badge?: string }[] = [
  { id: 'overview',    label: 'Overview'       },
  { id: 'hearings',   label: 'Hearings'        },
  { id: 'fees',       label: 'Fees & Revenue'  },
  { id: 'recovery',   label: 'Recovery Queue'  },
  { id: 'barristers', label: 'Barristers'      },
  { id: 'matters',    label: 'Matters'         },
  { id: 'operations', label: 'Operations'      },
];

export default function ReportsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const alertsQ = useQuery({ queryKey: ['rpt-alerts'], queryFn: fetchOperationalAlerts, staleTime: 60_000, refetchInterval: 120_000 });
  const alerts       = alertsQ.data ?? [];
  const criticalCount = alerts.filter((a) => a.level === 'critical').length;
  const urgentCount   = alerts.filter((a) => a.level !== 'info').length;

  return (
    <div>
      {/* Page header — PageHeader rather than a hand-rolled one, so this page carries the
          same back control, title scale and wrapping behaviour as every other page. The
          critical-alert chip rides along as the header's action. */}
      <PageHeader
        onBack={() => navigate(-1)}
        eyebrow="Intelligence"
        title="Chambers Reports"
        subtitle={`Operational intelligence for ${new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`}
        actions={
          criticalCount > 0 ? (
            <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 dark:border-red-500/30 dark:bg-red-500/10">
              <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" weight="fill" />
              <span className="text-sm font-semibold text-red-700 dark:text-red-300">{criticalCount} critical alert{criticalCount !== 1 ? 's' : ''} require attention</span>
            </div>
          ) : undefined
        }
      />

      {/* Alert ribbon */}
      <AlertRibbon alerts={alerts} />

      {/* Tab navigation */}
      {/* Seven tabs at 40px of padding each come to roughly 800px: on a phone everything from
          "Recovery Queue" rightwards — Barristers, Matters and Operations, which is where the
          alert badge lives — was off the screen with no way to reach it. The strip scrolls
          sideways instead. The overlap pixel sits on the SCROLLER rather than on each button, so
          nothing protrudes vertically for `overflow-y` to scroll. Unchanged on desktop, where
          the row has always fitted. */}
      <div className="no-scrollbar -mb-px mb-6 flex items-center gap-0.5 overflow-x-auto overflow-y-hidden border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-5 py-3 text-sm font-medium transition ${tab === t.id ? 'border-brand-600 text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
            {t.id === 'operations' && urgentCount > 0 && (
              <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white">{urgentCount}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview'    && <OverviewTab />}
      {tab === 'hearings'    && <HearingReadinessTab />}
      {tab === 'fees'        && <FeesTab />}
      {tab === 'recovery'    && <RecoveryTab />}
      {tab === 'barristers'  && <BarristersTab />}
      {tab === 'matters'     && <MattersTab />}
      {tab === 'operations'  && <OperationsTab />}
    </div>
  );
}
